import { describeDays, formatRange } from '../../lib/format/dates.ts';
import type { Physician, WeekView } from '../api.ts';
import { copy } from '../copy/en-CA.ts';
import { DayStrip } from './DayStrip.tsx';

const c = copy.schedule;

export function PhysicianName({ physician }: { physician: Physician | null }) {
  if (!physician) return <span className="unassigned">{c.unassigned}</span>;
  return (
    <span className="physician">
      <span className="dot" style={{ background: physician.colour }} aria-hidden="true" />
      <span className="physician-name">{physician.name}</span>
    </span>
  );
}

export function WeekBadges({ week }: { week: WeekView }) {
  return (
    <span className="badges">
      {week.kind === 'long-weekend' ? (
        <span className="badge lw">
          {c.kinds['long-weekend']}
          {week.longWeekendHolidays.length > 0 ? ` · ${week.longWeekendHolidays.join(', ')}` : ''}
        </span>
      ) : null}
      {week.kind === 'christmas' ? <span className="badge xmas">{c.kinds.christmas}</span> : null}
      {week.kind === 'new-year' ? <span className="badge ny">{c.kinds['new-year']}</span> : null}
      {week.specialOverride ? <span className="badge ok">{c.overridden}</span> : null}
      {week.manual ? <span className="badge ok">{c.manual}</span> : null}
      {week.conflicts.map((k) => (
        <span key={k} className="badge conf">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 9v4" />
            <path d="M12 17h.01" />
            <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
          </svg>
          {c.conflicts[k] ?? k}
        </span>
      ))}
    </span>
  );
}

export function rowClass(week: WeekView, mine: boolean): string {
  if (mine) return 'mine';
  if (week.physicianId !== null && week.conflicts.length > 0) return 'conf';
  if (week.physicianId === null) return 'conf';
  if (week.kind === 'long-weekend') return 'lw';
  if (week.kind === 'christmas') return 'xmas';
  if (week.kind === 'new-year') return 'ny';
  return '';
}

export interface WeekTableProps {
  weeks: WeekView[];
  physicians: Physician[];
  myPhysicianId: string | null;
  /** Extra cells per row, for the admin's controls. */
  renderControls?: (week: WeekView, index: number) => React.ReactNode;
  controlsHeader?: string;
  /** How many weeks at the top are folded away on screen (they still print). */
  hiddenEarlier?: number;
  /** The row on call today, marked "This week"; -1 for none. */
  nowIndex?: number;
  /** Admins only: the last column, who asked for what in each week. Never printed. */
  renderRequests?: (week: WeekView) => React.ReactNode;
}

/**
 * The weeks of a schedule. A table on wide screens; the same rows become
 * cards on a phone through CSS, so nothing needs sideways scrolling.
 */
export function WeekTable({ weeks, physicians, myPhysicianId, renderControls, controlsHeader, hiddenEarlier = 0, nowIndex = -1, renderRequests }: WeekTableProps) {
  const byId = new Map(physicians.map((p) => [p.id, p]));
  return (
    <div className="week-table" role="region" aria-label={c.tabs.weeks} tabIndex={0}>
      <table>
        <thead>
          <tr>
            <th className="col-range">{c.columns.onCall}</th>
            <th className="col-days">{c.columns.days}</th>
            <th className="col-phys">{c.columns.physician}</th>
            {renderControls ? <th className="col-controls">{controlsHeader ?? ''}</th> : null}
            <th className="col-notes">{c.columns.notes}</th>
            {renderRequests ? <th className="col-requests no-print">{c.requests.header}</th> : null}
          </tr>
        </thead>
        <tbody>
          {weeks.map((w, i) => {
            const mine = myPhysicianId !== null && w.physicianId === myPhysicianId;
            const classes = [rowClass(w, mine), i < hiddenEarlier ? 'earlier' : '', i === nowIndex ? 'now' : ''].filter(Boolean).join(' ');
            return (
              <tr key={w.weekStart} className={classes}>
                <td className="col-range">
                  {i === nowIndex ? <span className="badge now">{c.thisWeek}</span> : null}
                  <div className="range">{formatRange(w.onCallStart, w.onCallEnd)}</div>
                  <div className="muted small">{describeDays(w)}</div>
                </td>
                <td className="col-days">
                  <DayStrip week={w} />
                </td>
                <td className="col-phys">
                  <PhysicianName physician={w.physicianId ? (byId.get(w.physicianId) ?? null) : null} />
                  {mine ? <span className="badge mine">{c.you}</span> : null}
                </td>
                {renderControls ? <td className="col-controls">{renderControls(w, i)}</td> : null}
                <td className="col-notes">
                  <WeekBadges week={w} />
                </td>
                {renderRequests ? <td className="col-requests no-print">{renderRequests(w)}</td> : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
